<template>
  <div class="graph-filters">
    <v-expansion-panels multiple variant="accordion">
      <!-- dataslug -->
      <FacetCheckboxPanel
        title="Dataslug"
        v-model="local.dataslug"
        :facetMap="facets?.dataslug"
      />

      <!-- Optional: name (can be large) -->
      <FacetCheckboxPanel
        title="Name"
        v-model="local.name"
        :facetMap="facets?.name"
        :maxHeight="260"
      />

      <!-- Occupation -->
      <FacetCheckboxPanel
        title="Occupation"
        v-model="local.occupation"
        :facetMap="facets?.occupation"
        :maxHeight="320"
      />

      <!-- Exists: Abstract -->
      <ExistencePanel
        title="Abstract"
        v-model="local.exists.abstract"
        :counts="facets?.exists?.abstract"
      />

      <!-- Exists: Publication -->
      <ExistencePanel
        title="Publication"
        v-model="local.exists.publication"
        :counts="facets?.exists?.publication"
      />

      <!-- birthYear -->
      <RangePanel
        title="birthYear"
        v-model="local.birthYear"
        :min="rangeMin('birthYear')"
        :max="rangeMax('birthYear')"
      />

      <!-- birthDate -->
      <RangePanel
        title="birthDate"
        v-model="local.birthDate"
        :min="rangeMin('birthDate')"
        :max="rangeMax('birthDate')"
      />

      <!-- deathYear -->
      <RangePanel
        title="deathYear"
        v-model="local.deathYear"
        :min="rangeMin('deathYear')"
        :max="rangeMax('deathYear')"
      />

      <!-- deathDate -->
      <RangePanel
        title="deathDate"
        v-model="local.deathDate"
        :min="rangeMin('deathDate')"
        :max="rangeMax('deathDate')"
      />

      <!-- birthLocation -->
      <LocationCheckboxPanel
        title="birthLocation"
        v-model="local.birthLocation"
        :facetMap="facets?.birthLocation"
      />

      <!-- deathLocation -->
      <LocationCheckboxPanel
        title="deathLocation"
        v-model="local.deathLocation"
        :facetMap="facets?.deathLocation"
      />
    </v-expansion-panels>

    <div class="d-flex ga-2 mt-3">
      <v-btn variant="tonal" @click="resetAll">Reset</v-btn>
      <v-spacer />
      <v-btn color="primary" @click="applyNow">Apply</v-btn>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, watch } from "vue";
// ✅ Pfad ggf. anpassen:
// - wenn fqBuilder.js unter assets/js/filters liegt, ist es oft "../js/filters/fqBuilder"
// - oder "../../js/filters/fqBuilder" je nach Vue-Ordnerstruktur
import { buildFq } from "../js/filters/fqBuilder";

const props = defineProps({
  facets: { type: Object, default: () => ({}) },
  modelValue: { type: Object, default: null },
});

const emit = defineEmits(["update:modelValue", "change", "apply"]);

const defaultState = () => ({
  dataslug: [],
  name: [],
  occupation: [],
  birthLocation: [],
  deathLocation: [],
  birthYear: null,  // [min,max]
  deathYear: null,
  birthDate: null,
  deathDate: null,
  exists: {
    abstract: [],     // ["has", "missing"]
    publication: [],
  },
});

const local = reactive(defaultState());

watch(
  () => props.modelValue,
  (v) => {
    if (!v) return;
    Object.assign(local, defaultState(), v);
  },
  { immediate: true, deep: true }
);

function resetAll() {
  Object.assign(local, defaultState());
  // emit as change
  const state = toPlain(local);
  emit("update:modelValue", state);
  emit("change", state);
}

function applyNow() {
  const state = toPlain(local);
  const fq = buildFq(state);
  emit("update:modelValue", state);
  emit("change", state);
  emit("apply", { state, fq }); // ✅ app.js erwartet { fq }
}

function toPlain(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function rangeMin(field) {
  const r = props.facets?.ranges?.[field];
  const v = Number(r?.min ?? 0);
  return Number.isFinite(v) ? v : 0;
}
function rangeMax(field) {
  const r = props.facets?.ranges?.[field];
  const v = Number(r?.max ?? 0);
  return Number.isFinite(v) ? v : 0;
}
</script>

<script>
export default {
  name: "GraphFilterApp",
  components: {
    FacetCheckboxPanel: {
      name: "FacetCheckboxPanel",
      props: {
        title: { type: String, required: true },
        facetMap: { type: Object, default: () => ({}) }, // { value: count }
        modelValue: { type: Array, default: () => [] },
        maxHeight: { type: Number, default: 280 },
      },
      emits: ["update:modelValue"],
      computed: {
        options() {
          const m = this.facetMap || {};
          return Object.entries(m)
            .map(([value, count]) => ({
              value,
              label: String(value),
              count: Number(count) || 0,
            }))
            .sort((a, b) => b.count - a.count);
        },
        total() {
          return this.options.reduce((s, o) => s + o.count, 0);
        },
        proxy: {
          get() { return this.modelValue; },
          set(v) { this.$emit("update:modelValue", v); },
        },
      },
      template: `
        <v-expansion-panel>
          <v-expansion-panel-title>
            {{ title }}
            <span class="ms-2 text-medium-emphasis">({{ total }})</span>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <div :style="{maxHeight: maxHeight + 'px', overflowY: 'auto'}" class="d-flex flex-column ga-1">
              <v-checkbox
                v-for="opt in options"
                :key="title + ':' + opt.value"
                v-model="proxy"
                :value="opt.value"
                :label="\`\${opt.label} (\${opt.count})\`"
                density="compact"
                hide-details
              />
              <div v-if="!options.length" class="text-medium-emphasis">
                No values.
              </div>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      `,
    },

    LocationCheckboxPanel: {
      name: "LocationCheckboxPanel",
      props: {
        title: { type: String, required: true },
        facetMap: { type: Object, default: () => ({}) },
        modelValue: { type: Array, default: () => [] },
      },
      emits: ["update:modelValue"],
      methods: {
        lastSegmentLabel(value) {
          if (value == null) return "";
          let s = String(value);

          const parts = s.split(/[\/#]/).filter(Boolean);
          let last = parts.length ? parts[parts.length - 1] : s;

          try { last = decodeURIComponent(last); } catch (_) {}
          last = last.replaceAll("_", " ").trim();

          return last ? last.charAt(0).toUpperCase() + last.slice(1) : last;
        },
      },
      computed: {
        options() {
          const m = this.facetMap || {};
          return Object.entries(m)
            .map(([value, count]) => ({
              value,
              label: this.lastSegmentLabel(value),
              count: Number(count) || 0,
            }))
            .sort((a, b) => b.count - a.count);
        },
        total() {
          return this.options.reduce((s, o) => s + o.count, 0);
        },
        proxy: {
          get() { return this.modelValue; },
          set(v) { this.$emit("update:modelValue", v); },
        },
      },
      template: `
        <v-expansion-panel>
          <v-expansion-panel-title>
            {{ title }}
            <span class="ms-2 text-medium-emphasis">({{ total }})</span>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <div style="max-height: 280px; overflow-y: auto" class="d-flex flex-column ga-1">
              <v-checkbox
                v-for="opt in options"
                :key="title + ':' + opt.value"
                v-model="proxy"
                :value="opt.value"
                :label="\`\${opt.label} (\${opt.count})\`"
                density="compact"
                hide-details
              />
              <div v-if="!options.length" class="text-medium-emphasis">
                No values.
              </div>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      `,
    },

    ExistencePanel: {
      name: "ExistencePanel",
      props: {
        title: { type: String, required: true },
        counts: { type: Object, default: () => ({ has: 0, missing: 0 }) },
        modelValue: { type: Array, default: () => [] }, // ["has","missing"]
      },
      emits: ["update:modelValue"],
      computed: {
        total() {
          return (Number(this.counts?.has) || 0) + (Number(this.counts?.missing) || 0);
        },
        proxy: {
          get() { return this.modelValue; },
          set(v) { this.$emit("update:modelValue", v); },
        },
      },
      template: `
        <v-expansion-panel>
          <v-expansion-panel-title>
            {{ title }}
            <span class="ms-2 text-medium-emphasis">({{ total }})</span>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <div class="d-flex flex-column ga-1">
              <v-checkbox
                v-model="proxy"
                value="has"
                :label="\`hat (\${counts?.has || 0})\`"
                density="compact"
                hide-details
              />
              <v-checkbox
                v-model="proxy"
                value="missing"
                :label="\`hat nicht (\${counts?.missing || 0})\`"
                density="compact"
                hide-details
              />
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      `,
    },

    RangePanel: {
      name: "RangePanel",
      props: {
        title: { type: String, required: true },
        min: { type: Number, default: 0 },
        max: { type: Number, default: 0 },
        modelValue: { type: Array, default: null }, // [min,max]
      },
      emits: ["update:modelValue"],
      data() {
        return {
          internal: this.modelValue ?? (this.max > this.min ? [this.min, this.max] : null),
        };
      },
      watch: {
        modelValue: {
          handler(v) { this.internal = v ?? (this.max > this.min ? [this.min, this.max] : null); },
          deep: true,
        },
        min() {
          if (!this.modelValue && this.max > this.min) this.internal = [this.min, this.max];
        },
        max() {
          if (!this.modelValue && this.max > this.min) this.internal = [this.min, this.max];
        },
      },
      computed: {
        disabled() {
          return !(this.max > this.min);
        },
        labelRange() {
          if (!this.internal) return "—";
          return `${this.internal[0]}–${this.internal[1]}`;
        },
      },
      methods: {
        commit() {
          if (this.disabled) {
            this.$emit("update:modelValue", null);
            return;
          }
          this.$emit("update:modelValue", this.internal);
        },
        clearRange() {
          this.internal = this.max > this.min ? [this.min, this.max] : null;
          // optional: set null instead of full range
          this.$emit("update:modelValue", null);
        },
      },
      template: `
        <v-expansion-panel>
          <v-expansion-panel-title>
            {{ title }}
            <span class="ms-2 text-medium-emphasis">({{ labelRange }})</span>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <div v-if="disabled" class="text-medium-emphasis">
              No range available.
            </div>

            <div v-else>
              <v-range-slider
                v-model="internal"
                :min="min"
                :max="max"
                step="1"
                thumb-label="always"
                @end="commit"
              />
              <div class="d-flex ga-2 mt-2">
                <v-text-field
                  v-model.number="internal[0]"
                  type="number"
                  label="von"
                  density="compact"
                  hide-details
                  @change="commit"
                />
                <v-text-field
                  v-model.number="internal[1]"
                  type="number"
                  label="bis"
                  density="compact"
                  hide-details
                  @change="commit"
                />
                <v-btn variant="text" @click="clearRange">clear</v-btn>
              </div>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      `,
    },
  },
};
</script>

<style scoped>
.graph-filters {
  width: 100%;
}
</style>
